import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

export default function BugReports() {
  const { getAuthHeader } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const perPage = 20;

  // Dialogue history state
  const [loadingHistory, setLoadingHistory] = useState({});
  const [dialogueHistory, setDialogueHistory] = useState({});

  useEffect(() => {
    fetchReports();
  }, [page]);

  const fetchReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/bug-reports?page=${page}&limit=${perPage}`, {
        headers: {
          Authorization: getAuthHeader()
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bug reports');
      }

      const data = await response.json();
      setReports(data.reports);
      setHasMore(data.hasMore);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString();
  };

  const loadDialogueHistory = async (report) => {
    const reportId = report.id;
    const reportedText = report.assistantResponse || report.message?.text;
    const reportedMsgId = report.messageId;

    setLoadingHistory(prev => ({ ...prev, [reportId]: true }));
    try {
      let allMessages = [];
      let dialogueId = report.dialogueId;

      // If we have dialogueId, use it directly
      if (dialogueId) {
        const response = await fetch(`/api/admin/dialogue-messages/${dialogueId}?limit=100`, {
          headers: { Authorization: getAuthHeader() }
        });
        if (response.ok) {
          const data = await response.json();
          allMessages = data.messages || [];
        }
      }

      // If no dialogueId or no messages found, search user's dialogues
      if (allMessages.length === 0 && report.user) {
        // Get user's dialogues
        const dialoguesRes = await fetch(`/api/admin/user-dialogues?email=${encodeURIComponent(report.user)}&limit=20`, {
          headers: { Authorization: getAuthHeader() }
        });

        if (dialoguesRes.ok) {
          const dialoguesData = await dialoguesRes.json();
          const dialogues = dialoguesData.dialogues || [];

          // Search each dialogue for the message
          for (const dialogue of dialogues) {
            const msgRes = await fetch(`/api/admin/dialogue-messages/${dialogue.id}?limit=100`, {
              headers: { Authorization: getAuthHeader() }
            });

            if (msgRes.ok) {
              const msgData = await msgRes.json();
              const messages = msgData.messages || [];

              // Check if this dialogue contains the reported message
              const found = messages.some(msg =>
                msg.id === reportedMsgId ||
                (reportedText && msg.content && msg.content.includes(reportedText.substring(0, 50)))
              );

              if (found) {
                allMessages = messages;
                dialogueId = dialogue.id;
                break;
              }
            }
          }
        }
      }

      if (allMessages.length === 0) {
        alert('Could not find dialogue for this report');
        setLoadingHistory(prev => ({ ...prev, [reportId]: false }));
        return;
      }

      // Find the reported message by matching content or messageId
      let targetIndex = -1;
      for (let i = 0; i < allMessages.length; i++) {
        const msg = allMessages[i];
        if (msg.id === reportedMsgId ||
            (reportedText && msg.content && msg.content.includes(reportedText.substring(0, 50)))) {
          targetIndex = i;
          break;
        }
      }

      // Get 10 messages before, the target, and 2 after (up to 13 total)
      let relevantMessages;
      if (targetIndex >= 0) {
        const startIdx = Math.max(0, targetIndex - 10);
        const endIdx = Math.min(allMessages.length, targetIndex + 3);
        relevantMessages = allMessages.slice(startIdx, endIdx);
      } else {
        // If not found, show all messages (up to what we fetched)
        relevantMessages = allMessages;
      }

      // Mark which message is the reported one
      const reportedMsgIndex = relevantMessages.findIndex(msg =>
        msg.id === reportedMsgId ||
        (reportedText && msg.content && msg.content.includes(reportedText.substring(0, 50)))
      );

      setDialogueHistory(prev => ({
        ...prev,
        [reportId]: {
          messages: relevantMessages,
          found: targetIndex >= 0,
          totalMessages: allMessages.length,
          dialogueId,
          reportedIndex: reportedMsgIndex
        }
      }));
    } catch (err) {
      console.error('Failed to load dialogue:', err);
      alert('Network error loading dialogue');
    } finally {
      setLoadingHistory(prev => ({ ...prev, [reportId]: false }));
    }
  };

  const totalPages = Math.ceil(total / perPage);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'auto',
      background: 'var(--bg-primary, #f5f5f5)'
    }}>
      <div style={{
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        fontFamily: 'system-ui, -apple-system, sans-serif'
      }}>
      <h1 style={{ marginBottom: '20px', color: 'var(--text-color, #333)' }}>Bug Reports</h1>

      {loading && <div style={{ padding: '20px', textAlign: 'center' }}>Loading...</div>}

      {error && (
        <div style={{
          padding: '12px',
          background: '#fee',
          border: '1px solid #fcc',
          borderRadius: '8px',
          color: '#c00',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {!loading && !error && reports.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          No bug reports found.
        </div>
      )}

      {!loading && reports.length > 0 && (
        <>
          <div style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
            Showing {(page - 1) * perPage + 1} - {Math.min(page * perPage, total)} of {total} reports
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {reports.map((report, index) => (
              <div
                key={report.id || index}
                style={{
                  background: 'var(--bg-secondary, #fff)',
                  border: '1px solid var(--border-color, #ddd)',
                  borderRadius: '8px',
                  padding: '16px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '12px',
                  flexWrap: 'wrap',
                  gap: '8px'
                }}>
                  <div>
                    <span style={{
                      background: report.type === 'feedback' ? '#e3f2fd' : '#fff3e0',
                      color: report.type === 'feedback' ? '#1976d2' : '#f57c00',
                      padding: '2px 8px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      fontWeight: '500',
                      marginRight: '8px'
                    }}>
                      {report.type || 'bug_report'}
                    </span>
                    {report.jiraTicket && (
                      <a
                        href={`https://distributedglobal.atlassian.net/browse/${report.jiraTicket}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          color: '#007bff',
                          textDecoration: 'none',
                          fontWeight: '500'
                        }}
                      >
                        {report.jiraTicket}
                      </a>
                    )}
                  </div>
                  <span style={{ color: '#666', fontSize: '13px' }}>
                    {formatDate(report.timestamp)}
                  </span>
                </div>

                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ color: 'var(--text-color, #333)' }}>Reporter:</strong>{' '}
                  <span style={{ color: 'var(--text-secondary, #666)' }}>
                    {report.reporterName || report.reporter || report.user || 'Unknown'}
                  </span>
                </div>

                {report.description && (
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: 'var(--text-color, #333)' }}>Description:</strong>
                    <div style={{
                      marginTop: '4px',
                      padding: '8px 12px',
                      background: 'var(--bg-primary, #f5f5f5)',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      fontSize: '14px',
                      color: 'var(--text-color, #333)'
                    }}>
                      {report.description}
                    </div>
                  </div>
                )}

                {report.userInput && (
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: 'var(--text-color, #333)' }}>User Input:</strong>
                    <div style={{
                      marginTop: '4px',
                      padding: '8px 12px',
                      background: 'var(--bg-primary, #f5f5f5)',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      fontSize: '14px',
                      color: 'var(--text-color, #333)'
                    }}>
                      {report.userInput}
                    </div>
                  </div>
                )}

                {report.assistantResponse && (
                  <div style={{ marginBottom: '12px' }}>
                    <strong style={{ color: 'var(--text-color, #333)' }}>Assistant Response:</strong>
                    <div style={{
                      marginTop: '4px',
                      padding: '8px 12px',
                      background: 'var(--bg-primary, #f5f5f5)',
                      borderRadius: '4px',
                      whiteSpace: 'pre-wrap',
                      fontSize: '14px',
                      maxHeight: '200px',
                      overflow: 'auto',
                      color: 'var(--text-color, #333)'
                    }}>
                      {report.assistantResponse}
                    </div>
                  </div>
                )}

                {report.reportText && (
                  <details style={{ marginTop: '8px' }}>
                    <summary style={{ cursor: 'pointer', color: '#666', fontSize: '13px' }}>
                      Full Report Text
                    </summary>
                    <pre style={{
                      marginTop: '8px',
                      padding: '12px',
                      background: '#f5f5f5',
                      borderRadius: '4px',
                      overflow: 'auto',
                      fontSize: '12px',
                      maxHeight: '300px'
                    }}>
                      {report.reportText}
                    </pre>
                  </details>
                )}

                {/* Dialogue History Section */}
                <div style={{ marginTop: '12px', borderTop: '1px solid var(--border-color, #eee)', paddingTop: '12px' }}>
                  {!dialogueHistory[report.id] ? (
                    <button
                      onClick={() => loadDialogueHistory(report)}
                      disabled={loadingHistory[report.id]}
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        background: '#f0f0f0',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: loadingHistory[report.id] ? 'wait' : 'pointer'
                      }}
                    >
                      {loadingHistory[report.id] ? 'Searching...' : '📜 Load Message History'}
                    </button>
                  ) : (
                    <div style={{
                      background: '#fafafa',
                      border: '1px solid #eee',
                      borderRadius: '6px',
                      padding: '12px'
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '8px', color: '#666' }}>
                        {dialogueHistory[report.id].found
                          ? `Message History (showing ${dialogueHistory[report.id].messages.length} of ${dialogueHistory[report.id].totalMessages} total in dialogue):`
                          : `Could not find exact message, showing all ${dialogueHistory[report.id].messages.length} messages from dialogue:`}
                      </div>
                      {dialogueHistory[report.id].totalMessages <= dialogueHistory[report.id].messages.length && (
                        <div style={{ fontSize: '11px', color: '#999', marginBottom: '8px', fontStyle: 'italic' }}>
                          (This is the entire dialogue)
                        </div>
                      )}
                      {dialogueHistory[report.id].messages.map((msg, idx) => {
                        const isReported = idx === dialogueHistory[report.id].reportedIndex;
                        return (
                          <div
                            key={msg.id || idx}
                            style={{
                              padding: '8px 10px',
                              marginBottom: '6px',
                              background: isReported ? '#fff3cd' : msg.role === 'user' ? '#e3f2fd' : '#fff',
                              border: isReported ? '2px solid #ffc107' : '1px solid #ddd',
                              borderRadius: '6px',
                              fontSize: '13px'
                            }}
                          >
                            <div style={{ fontSize: '11px', color: '#888', marginBottom: '4px' }}>
                              <strong>{msg.role === 'user' ? '👤 User' : '🤖 Assistant'}</strong>
                              {isReported && <span style={{ color: '#856404', marginLeft: '8px' }}>⚠️ REPORTED</span>}
                              {' · '}
                              {formatDate(msg.created_at)}
                            </div>
                            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                              {msg.content}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '12px',
            marginTop: '24px',
            paddingBottom: '40px'
          }}>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid var(--border-color, #ddd)',
                background: page === 1 ? '#f5f5f5' : 'var(--bg-secondary, #fff)',
                cursor: page === 1 ? 'not-allowed' : 'pointer',
                opacity: page === 1 ? 0.5 : 1,
                color: 'var(--text-color, #333)'
              }}
            >
              Previous
            </button>
            <span style={{ color: 'var(--text-secondary, #666)' }}>
              Page {page} of {totalPages || 1}
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={!hasMore}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: '1px solid var(--border-color, #ddd)',
                background: !hasMore ? '#f5f5f5' : 'var(--bg-secondary, #fff)',
                cursor: !hasMore ? 'not-allowed' : 'pointer',
                opacity: !hasMore ? 0.5 : 1,
                color: 'var(--text-color, #333)'
              }}
            >
              Next
            </button>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
